import { Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { isString } from 'lodash';
import { memo } from 'react';
import { CustomButton } from 'src/components/button/Button';
import type { SimpleDialogProps } from 'src/components/modal';
import { lang } from 'src/utils';

import { Modal } from '../Modal';

const BootstrapDialog = styled(Modal)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
  },
}));

const SubTitle = styled(Typography)`
  word-wrap: break-word;
`;

export const SimpleDialog = memo((props: SimpleDialogProps) => {
  const actionColor =
    props.actionColor ??
    (props.confirmText === lang.deleteText ? 'error' : 'primary');
  const cancelButtonColor =
    props.cancelButtonColor ??
    (!props.cancelText ||
    props.cancelText === lang.cancel ||
    props.cancelText === lang.cancelText ||
    props.cancelText === lang.noText ||
    props.cancelText === lang.navigateBlockerModal.cancelText ||
    props.cancelText === lang.close
      ? 'secondary'
      : actionColor === 'error'
      ? 'inherit'
      : actionColor);

  return (
    <BootstrapDialog
      {...props}
      onClose={props.disableClose ? undefined : props.onClose}
      maxWidth={props.modalSize ? props.modalSize : 'xs'}
      fullWidth>
      {isString(props.title) ? (
        <DialogTitle
          id="customized-dialog-title"
          sx={{ paddingRight: props.adjustTitlePadding ? '40px' : undefined }}>
          {props.title}
        </DialogTitle>
      ) : (
        props.title
      )}

      {!props.disableClose && props.onClickCancel && (
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>
      )}

      {props.subTitle && (
        <DialogContent>
          <SubTitle component={isString(props.subTitle) ? undefined : 'span'}>
            {props.subTitle}
          </SubTitle>
        </DialogContent>
      )}
      {props.isInputRequired && (
        <DialogContent>
          <TextField
            fullWidth
            required
            value={props.inputValue}
            onChange={props.setInputValue}
          />
        </DialogContent>
      )}

      {props.children && <DialogContent>{props.children}</DialogContent>}
      {!props.hideButton && (
        <DialogActions>
          {!props.disableClose && props.onClickCancel && (
            <CustomButton
              color={cancelButtonColor}
              btnColor={cancelButtonColor}
              variant="text"
              onClick={props.onClickCancel}>
              {props.cancelText || lang.cancel}
            </CustomButton>
          )}
          {!props.hideConfirm && (
            <LoadingButton
              color={actionColor}
              variant="contained"
              onClick={props.onClickConfirm}
              loading={props.isLoading}
              disabled={props.disabled}>
              {props.confirmText || lang.save}
            </LoadingButton>
          )}
        </DialogActions>
      )}
    </BootstrapDialog>
  );
});
